import { useGetUsersQuery, type TUser } from "@/shared/services";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store/analytics.store";
import { ANALYTICS_QUERY } from "@pages/analytics/types";
import {
  IconCheck,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

type VersionType = {
  version_id: string;
  creator_id: string;
  [key: string]: unknown;
};

export const OwnerFilter = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const { user, setUser } = useAnalyticsStoreShallow((s) => ({
    user: s.user,
    setUser: s.setUser,
  }));

  const { data: users } = useGetUsersQuery();

  const { data: response, isLoading } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.VERSIONS],
    enabled: isOpen,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleOwnerChange = useCallback(
    (owner: TUser) => {
      setUser({ id: owner.oktaId, name: owner.firstName });
      setIsOpen(false);
    },
    [setUser],
  );

  const handleClear = useCallback(() => {
    setUser({ id: "", name: "" });
    setIsOpen(false);
  }, [setUser]);

  const versionsData = response?.[ANALYTICS_QUERY.VERSIONS] as
    | VersionType[]
    | undefined;

  const filteredUsers = useMemo(() => {
    // First filter users by those who have created versions
    let availableUsers = users;
    if (versionsData && users) {
      const creatorIds = new Set(
        versionsData.map((version: VersionType) => version.creator_id),
      );
      availableUsers = users.filter((u: TUser) => creatorIds.has(u.oktaId));
    }

    // Then filter by search term
    if (!debouncedSearchTerm.trim()) return availableUsers;
    const search = debouncedSearchTerm.toLowerCase();
    return availableUsers?.filter((u: TUser) =>
      u.firstName.toLowerCase().includes(search),
    );
  }, [users, debouncedSearchTerm, versionsData]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
      }, 250);
    }
  }, []);

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-start"
      triggerWidth
    >
      <Popover.Trigger
        className={cn(
          "tw-flex tw-h-9 tw-cursor-pointer tw-max-w-40 active:!tw-outline-offset-0 active:!tw-outline-1 active:!tw-outline-primary tw-items-center tw-shadow-2xs tw-gap-1 tw-rounded-lg tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-px-2 tw-transition-colors !tw-outline-offset-0 tw-duration-100 hover:!tw-outline-primary -xl:tw-w-full -xl:tw-max-w-[700px]",
          {
            "!tw-outline-primary": isOpen || !!user.id,
          },
        )}
      >
        <span className="tw-block tw-px-1 tw-truncate tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
          {user.name || "Owner"}
        </span>
        <IconSelector className="tw-size-4 tw-text-blue-500 tw-ml-auto" />
        {user.id && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                handleClear();
              }
            }}
            role="button"
            tabIndex={0}
            className="tw-ml-1 tw-flex tw-h-full tw-items-center tw-justify-center tw-border-l tw-border-solid tw-border-primary tw-pl-2"
          >
            <IconX className="tw-size-4 tw-text-primary" />
          </div>
        )}
      </Popover.Trigger>
      <Popover.Content className="tw-rounded-lg !tw-w-auto tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-shadow-md">
        <div className="tw-relative tw-h-9 tw-px-3 tw-border-b tw-border-b-solid tw-border-b-blue-100">
          <IconSearch className="tw-size-4 tw-text-blue-500 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3" />
          <input
            type="text"
            placeholder="Search owner"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tw-pl-6 tw-outline-none tw-w-full tw-h-full tw-rounded-t-lg tw-text-sm tw-font-normal tw-text-blue placeholder:tw-text-blue-500"
          />
        </div>
        <ScrollArea maxHeight={250}>
          <div className="tw-p-1 tw-space-y-0.5 tw-max-w-full tw-min-w-0">
            {isLoading ? (
              <OwnerSkeleton />
            ) : filteredUsers?.length === 0 ? (
              <div className="tw-py-3 tw-text-center tw-text-sm tw-text-blue-500">
                No owners available
              </div>
            ) : (
              filteredUsers?.map((u: TUser) => (
                <button
                  type="button"
                  key={u.oktaId}
                  onClick={() => handleOwnerChange(u)}
                  className={cn(
                    "tw-flex tw-h-9 tw-w-52 tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-blue-100 hover:tw-bg-blue-100":
                        user.id === u.oktaId,
                    },
                  )}
                >
                  <span className="tw-flex-1 tw-min-w-0 tw-text-left tw-truncate tw-whitespace-nowrap">
                    {u.firstName}
                  </span>
                  {user.id === u.oktaId && (
                    <IconCheck className="tw-size-4 tw-text-blue tw-ml-3 tw-flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover>
  );
});

const OWNER_SKELETON_WIDTHS = [64, 98, 41, 82, 53];

const OwnerSkeleton = () => {
  return (
    <>
      {OWNER_SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};
